export type AdminPaymentLinkStatus =
  | 'initiated'
  | 'pending'
  | 'paid'
  | 'failed'
  | 'canceled'
  | 'expired';

export type AdminPaymentMode = 'one_time' | 'recurring';

export interface AdminPaymentLineItem {
  id: string;
  label: string;
  quantity: number;
  unitAmount: number;
  lineTotal: number;
  currencyCode: string;
}

export interface AdminPaymentLink {
  id: string;
  paymentLinkKey: string;
  checkoutToken: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  serviceId: string;
  serviceName: string;
  paymentMode: AdminPaymentMode;
  linkedSubscriptionId: string;
  linkedSubscriptionNumber: string;
  amount: number;
  lineItems: AdminPaymentLineItem[];
  currencyCode: string;
  quoteNumber: string;
  quoteValidUntil: string;
  paymentUrl: string;
  quoteUrl: string;
  providerId: string;
  providerType: string;
  emailTemplateAlias: string;
  emailSentAt: string;
  lastSentTo: string;
  notes: string;
  metadata: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sessionStatus: AdminPaymentLinkStatus;
  lastStatus: string;
  paidAt: string;
  canceledAt: string;
  failedAt: string;
  recurringInterval: string;
  recurringStartDate: string;
  recurringAmount: number;
  recurringStatus: string;
  recurringActivatedAt: string;
  mollieCustomerId: string;
  mollieSubscriptionId: string;
}

export interface CreateAdminPaymentLinkInput {
  paymentMode?: AdminPaymentMode;
  linkedSubscriptionId?: string;
  linkedSubscriptionNumber?: string;
  recurringInterval?: string;
  recurringStartDate?: string;
  recurringAmount?: number;
  mollieCustomerId?: string;
  lineItems?: Array<{
    id?: string;
    label: string;
    quantity?: number;
    unitAmount: number;
  }>;
  clientId?: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  clientCompany?: string;
  serviceId?: string;
  serviceName: string;
  amount: number;
  currencyCode: string;
  quoteNumber?: string;
  quoteValidUntil?: string;
  clientMessage?: string;
  notes?: string;
  sendEmail?: boolean;
  emailTemplateAlias?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toPositiveInt = (value: unknown, fallback = 1): number => {
  const parsed = Math.trunc(toNumber(value));
  return parsed > 0 ? parsed : fallback;
};

const RECURRING_PAYMENT_MODE_VALUES = new Set([
  'recurring',
  'recurrent',
  'subscription',
  'sub',
  'abonnement',
  'abonnement_recurrent',
  'abonnement-recurring',
  'mensuel',
  'monthly',
]);

const ONE_TIME_PAYMENT_MODE_VALUES = new Set([
  'one_time',
  'one-time',
  'onetime',
  'oneoff',
  'single',
  'once',
  'unique',
  'paiement_unique',
]);

const normalizePaymentModeValue = (value: unknown): AdminPaymentMode | '' => {
  const normalized = String(value || '').toLowerCase().trim();
  if (!normalized) {
    return '';
  }

  if (RECURRING_PAYMENT_MODE_VALUES.has(normalized)) {
    return 'recurring';
  }

  if (ONE_TIME_PAYMENT_MODE_VALUES.has(normalized)) {
    return 'one_time';
  }

  return '';
};

const normalizeLineItems = (
  rawValue: unknown,
  currencyCode: string,
  fallbackServiceName: string,
  fallbackAmount: number
): AdminPaymentLineItem[] => {
  const source = Array.isArray(rawValue) ? rawValue : [];
  const normalized = source
    .map((entry, index) => {
      if (!isRecord(entry)) {
        return null;
      }

      const label = String(
        entry.label ?? entry.name ?? entry.serviceName ?? ''
      ).trim();
      const quantity = toPositiveInt(entry.quantity, 1);
      const unitAmount = toNumber(entry.unitAmount ?? entry.amount);
      const lineTotalRaw = toNumber(entry.lineTotal);
      const lineTotal = lineTotalRaw > 0 ? lineTotalRaw : unitAmount * quantity;

      if (!label || lineTotal <= 0) {
        return null;
      }

      return {
        id: String(entry.id ?? `item_${index + 1}`),
        label,
        quantity,
        unitAmount,
        lineTotal,
        currencyCode: String(entry.currencyCode ?? currencyCode ?? 'EUR').toUpperCase(),
      } satisfies AdminPaymentLineItem;
    })
    .filter((entry): entry is AdminPaymentLineItem => Boolean(entry));

  if (normalized.length > 0) {
    return normalized;
  }

  if (fallbackAmount > 0) {
    return [
      {
        id: 'item_1',
        label: fallbackServiceName || 'Service',
        quantity: 1,
        unitAmount: fallbackAmount,
        lineTotal: fallbackAmount,
        currencyCode: (currencyCode || 'EUR').toUpperCase(),
      },
    ];
  }

  return [];
};

const hydratePaymentLink = (input: AdminPaymentLink): AdminPaymentLink => {
  const source = input as unknown as Record<string, unknown>;
  const metadata = isRecord(source.metadata) ? source.metadata : null;
  const rawLineItems =
    source.lineItems ??
    (metadata ? metadata.lineItems ?? metadata.line_items : undefined);

  const currencyCode = String(input.currencyCode || 'EUR').toUpperCase();
  const lineItems = normalizeLineItems(
    rawLineItems,
    currencyCode,
    input.serviceName,
    Number(input.amount || 0)
  );
  const rawPaymentMode =
    source.paymentMode ??
    (metadata ? metadata.paymentMode ?? metadata.payment_mode : undefined);
  const normalizedRawPaymentMode = normalizePaymentModeValue(rawPaymentMode);
  const hasRecurringHints = Boolean(
    String(
      source.linkedSubscriptionId ??
        (metadata ? metadata.linkedSubscriptionId : undefined) ??
        ''
    ).trim() ||
      String(
        source.linkedSubscriptionNumber ??
          (metadata ? metadata.linkedSubscriptionNumber : undefined) ??
          ''
      ).trim() ||
      String(
        source.recurringInterval ??
          (metadata ? metadata.recurringInterval : undefined) ??
          ''
      ).trim() ||
      String(
        source.recurringStartDate ??
          (metadata ? metadata.recurringStartDate : undefined) ??
          ''
      ).trim() ||
      String(
        source.mollieCustomerId ??
          (metadata ? metadata.mollieCustomerId : undefined) ??
          ''
      ).trim() ||
      String(
        source.mollieSubscriptionId ??
          (metadata ? metadata.mollieSubscriptionId : undefined) ??
          ''
      ).trim() ||
      Number(
        source.recurringAmount ??
          (metadata ? metadata.recurringAmount : undefined) ??
          0
      ) > 0
  );
  const paymentMode: AdminPaymentMode =
    normalizedRawPaymentMode !== ''
      ? normalizedRawPaymentMode
      : hasRecurringHints
        ? 'recurring'
        : 'one_time';

  return {
    ...input,
    paymentMode,
    linkedSubscriptionId: String(
      source.linkedSubscriptionId ??
        (metadata ? metadata.linkedSubscriptionId : undefined) ??
        ''
    ),
    linkedSubscriptionNumber: String(
      source.linkedSubscriptionNumber ??
        (metadata ? metadata.linkedSubscriptionNumber : undefined) ??
        ''
    ),
    lineItems,
    recurringInterval: String(
      source.recurringInterval ??
        (metadata ? metadata.recurringInterval : undefined) ??
        ''
    ),
    recurringStartDate: String(
      source.recurringStartDate ??
        (metadata ? metadata.recurringStartDate : undefined) ??
        ''
    ),
    recurringAmount: Number(
      source.recurringAmount ??
        (metadata ? metadata.recurringAmount : undefined) ??
        (paymentMode === 'recurring' ? input.amount : 0)
    ),
    recurringStatus: String(
      source.recurringStatus ??
        (metadata ? metadata.recurringStatus : undefined) ??
        (paymentMode === 'recurring' ? 'pending' : 'none')
    ),
    recurringActivatedAt: String(
      source.recurringActivatedAt ??
        (metadata ? metadata.recurringActivatedAt : undefined) ??
        ''
    ),
    mollieCustomerId: String(
      source.mollieCustomerId ??
        (metadata ? metadata.mollieCustomerId : undefined) ??
        ''
    ),
    mollieSubscriptionId: String(
      source.mollieSubscriptionId ??
        (metadata ? metadata.mollieSubscriptionId : undefined) ??
        ''
    ),
  };
};

const parseResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.error || 'Erreur admin paiements') as Error & {
      retryable?: boolean;
    };
    error.retryable = response.status === 404 || response.status === 405;
    throw error;
  }

  if (!payload || typeof payload !== 'object') {
    const error = new Error('Réponse serveur paiements invalide.') as Error & {
      retryable?: boolean;
    };
    error.retryable = true;
    throw error;
  }

  return payload as T;
};

interface RequestAttempt {
  url: string;
  init: RequestInit;
}

const requestWithFallback = async <T,>(
  attempts: RequestAttempt[],
  validate: (payload: unknown) => boolean
): Promise<T> => {
  let lastError: unknown = null;

  for (const attempt of attempts) {
    try {
      const response = await fetch(attempt.url, attempt.init);
      const payload = await parseResponse<T>(response);
      if (!validate(payload)) {
        throw new Error('Réponse serveur paiements incomplète.');
      }

      return payload;
    } catch (error) {
      const candidate =
        typeof error === 'object' && error !== null
          ? (error as { retryable?: boolean })
          : null;

      if (candidate?.retryable === false) {
        throw error instanceof Error
          ? error
          : new Error('Erreur admin paiements');
      }
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Erreur admin paiements');
};

export async function getAdminPaymentLinks(): Promise<AdminPaymentLink[]> {
  const payload = await requestWithFallback<{ ok: true; paymentLinks: AdminPaymentLink[] }>(
    [
      {
        url: '/paymentLinks.php',
        init: {
          credentials: 'include',
        },
      },
      {
        url: '/api/admin/payment-links',
        init: {
          credentials: 'include',
        },
      },
    ],
    (candidate) => Array.isArray((candidate as { paymentLinks?: unknown })?.paymentLinks)
  );

  return (payload.paymentLinks || []).map(hydratePaymentLink);
}

export async function createAdminPaymentLink(
  input: CreateAdminPaymentLinkInput
): Promise<{ paymentLink: AdminPaymentLink; emailSent: boolean; emailError: string }> {
  const hasRecurringInputHints = Boolean(
    String(input.linkedSubscriptionId || '').trim() ||
      String(input.linkedSubscriptionNumber || '').trim() ||
      String(input.recurringInterval || '').trim() ||
      String(input.recurringStartDate || '').trim() ||
      String(input.mollieCustomerId || '').trim() ||
      Number(input.recurringAmount || 0) > 0
  );
  const normalizedInputPaymentMode = normalizePaymentModeValue(input.paymentMode);
  const payloadInput: CreateAdminPaymentLinkInput = {
    ...input,
    paymentMode:
      normalizedInputPaymentMode !== ''
        ? normalizedInputPaymentMode
        : hasRecurringInputHints
          ? 'recurring'
          : input.paymentMode,
  };
  const body = JSON.stringify(payloadInput);
  const payload = await requestWithFallback<{
    ok: true;
    paymentLink: AdminPaymentLink;
    emailSent: boolean;
    emailError: string;
  }>(
    [
      {
        url: '/paymentLinks.php',
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        },
      },
      {
        url: '/api/admin/payment-links',
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        },
      },
    ],
    (candidate) => Boolean((candidate as { paymentLink?: unknown })?.paymentLink)
  );

  return {
    ...payload,
    paymentLink: hydratePaymentLink(payload.paymentLink),
  };
}

export async function deleteAdminPaymentLink(paymentLinkKey: string): Promise<{
  paymentLinkKey: string;
  quoteNumber: string;
  ordersRemoved: number;
  subscriptionsRemoved: number;
}> {
  const resolvedKey = (paymentLinkKey || '').trim();
  if (!resolvedKey) {
    throw new Error('La clé du lien de paiement est obligatoire.');
  }

  const body = JSON.stringify({
    action: 'delete',
    paymentLinkKey: resolvedKey,
  });

  const payload = await requestWithFallback<{
    ok: true;
    deleted: {
      paymentLinkKey?: string;
      quoteNumber?: string;
      ordersRemoved?: number;
      subscriptionsRemoved?: number;
    };
  }>(
    [
      {
        url: '/paymentLinks.php',
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        },
      },
      {
        url: '/api/admin/payment-links',
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        },
      },
    ],
    (candidate) => Boolean((candidate as { deleted?: unknown })?.deleted)
  );

  return {
    paymentLinkKey: String(payload.deleted?.paymentLinkKey || resolvedKey),
    quoteNumber: String(payload.deleted?.quoteNumber || ''),
    ordersRemoved: Number(payload.deleted?.ordersRemoved || 0),
    subscriptionsRemoved: Number(payload.deleted?.subscriptionsRemoved || 0),
  };
}

export async function resendAdminPaymentLinkEmail(
  paymentLinkKey: string,
  templateAlias = ''
): Promise<AdminPaymentLink> {
  const body = JSON.stringify({
    paymentLinkKey,
    templateAlias,
  });

  const payload = await requestWithFallback<{ ok: true; paymentLink: AdminPaymentLink }>(
    [
      {
        url: '/paymentLinkSend.php',
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        },
      },
      {
        url: '/api/admin/payment-link-send',
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        },
      },
    ],
    (candidate) => Boolean((candidate as { paymentLink?: unknown })?.paymentLink)
  );

  return hydratePaymentLink(payload.paymentLink);
}

export async function getPublicPaymentLink(paymentLinkKey: string): Promise<AdminPaymentLink> {
  const query = new URLSearchParams({
    key: paymentLinkKey,
  });

  const payload = await requestWithFallback<{ ok: true; paymentLink: AdminPaymentLink }>(
    [
      {
        url: `/publicPaymentLink.php?${query.toString()}`,
        init: {},
      },
      {
        url: `/api/public/payment-link?${query.toString()}`,
        init: {},
      },
    ],
    (candidate) => Boolean((candidate as { paymentLink?: unknown })?.paymentLink)
  );

  return hydratePaymentLink(payload.paymentLink);
}

export function getInvoicePdfUrl(paymentLinkKey: string): string {
  const resolvedKey = (paymentLinkKey || '').trim();
  if (!resolvedKey) {
    return '';
  }

  return `/facture/${encodeURIComponent(resolvedKey)}.pdf`;
}

export const adminPaymentLinkService = {
  getPaymentLinks: getAdminPaymentLinks,
  createPaymentLink: createAdminPaymentLink,
  removePaymentLink: deleteAdminPaymentLink,
  resendEmail: resendAdminPaymentLinkEmail,
  getPublicPaymentLink,
  getInvoicePdfUrl,
};
